import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, RefreshCw } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  channelSubtitle, groupChannels, searchChannels, windowList,
} from "./scannerChannels";

// Searchable source-channel picker. Renders from an already-loaded list — it
// never fetches on open or on keystroke; refreshing is an explicit action the
// parent wires to POST /accounts/{id}/channels/refresh.
export function ChannelPicker({ channels, value, onChange, disabled, onRefresh, refreshing, status, statusTone = "muted" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 120);
    return () => clearTimeout(t);
  }, [query]);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 0); else setQuery(""); }, [open]);

  const selected = useMemo(() => channels.find((c) => c.id === String(value)) || null, [channels, value]);
  const filtered = useMemo(() => searchChannels(channels, debounced), [channels, debounced]);
  const { visible, hidden } = useMemo(() => windowList(filtered), [filtered]);
  const grouped = useMemo(() => groupChannels(visible), [visible]);

  const toneCls = { muted: "text-[var(--tg-muted)]", error: "text-[var(--c-rose)]", warn: "text-[var(--c-amber)]", ok: "text-[var(--tg-faint)]" }[statusTone] || "text-[var(--tg-muted)]";

  const Row = ({ c }) => (
    <button
      type="button"
      role="option"
      aria-selected={c.id === String(value)}
      data-testid={`scanner-channel-${c.id}`}
      onClick={() => { onChange(c.id); setOpen(false); }}
      className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded text-left text-sm ${c.id === String(value) ? "bg-[rgb(var(--c-sky-rgb)/0.12)]" : "hover:bg-[var(--tg-hover)]"}`}
    >
      <span className="min-w-0">
        <span className="block truncate">{c.title}</span>
        <span className="block truncate text-[11px] text-[var(--tg-faint)]">{channelSubtitle(c)}</span>
      </span>
      {c.id === String(value) && <Check className="h-3.5 w-3.5 shrink-0 text-[var(--c-sky)]" />}
    </button>
  );

  return (
    <div className="space-y-1">
      <div className="flex gap-1.5">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label="Source channel"
              aria-expanded={open}
              data-testid="scanner-channel-trigger"
              className="flex-1 min-w-0 rounded-md border border-[var(--tg-border)] bg-[var(--tg-bg)] text-[var(--tg-strong)] px-3 py-2 text-sm flex items-center justify-between gap-2 disabled:opacity-50"
            >
              <span className="min-w-0 truncate text-left">
                {selected ? (
                  <>
                    <span className="block truncate">{selected.title}</span>
                    <span className="block truncate text-[11px] text-[var(--tg-faint)]">{channelSubtitle(selected)}</span>
                  </>
                ) : <span className="text-[var(--tg-faint)]">{channels.length ? "Choose channel" : "No channels loaded"}</span>}
              </span>
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-2 bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search channels… (title, @username, ID)"
              aria-label="Search channels"
              data-testid="scanner-channel-search"
              className="w-full mb-2 rounded-md border border-[var(--tg-border)] bg-[var(--tg-bg)] px-2.5 py-1.5 text-sm"
            />
            <div className="max-h-72 overflow-y-auto" role="listbox" aria-label="Channels">
              {filtered.length === 0 && (
                <p className="px-2 py-3 text-xs text-[var(--tg-faint)]" data-testid="scanner-channel-nomatch">
                  {channels.length ? "No channels match." : "No channels loaded for this account."}
                </p>
              )}
              {grouped.channels.length > 0 && (
                <>
                  {grouped.groups.length > 0 && <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-[var(--tg-faint)]">Channels</div>}
                  {grouped.channels.map((c) => <Row key={c.id} c={c} />)}
                </>
              )}
              {grouped.groups.length > 0 && (
                <>
                  <div className="px-2 pt-2 pb-0.5 text-[10px] uppercase tracking-wider text-[var(--tg-faint)]">Groups</div>
                  {grouped.groups.map((c) => <Row key={c.id} c={c} />)}
                </>
              )}
              {hidden > 0 && (
                <p className="px-2 py-2 text-[11px] text-[var(--tg-faint)]" data-testid="scanner-channel-more">
                  {hidden.toLocaleString()} more — keep typing to narrow the list.
                </p>
              )}
            </div>
          </PopoverContent>
        </Popover>
        <Button
          type="button"
          variant="outline"
          size="icon"
          title="Refresh channels (one Telegram sync for this account)"
          aria-label="Refresh channels"
          data-testid="scanner-refresh-channels"
          disabled={disabled || refreshing}
          onClick={onRefresh}
          className="shrink-0 border-[var(--tg-border)]"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
        </Button>
      </div>
      {status && <p className={`text-[11px] ${toneCls}`} data-testid="scanner-channel-status">{status}</p>}
    </div>
  );
}
